import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  Snackbar
} from '@mui/material';
import { TrendingDown } from '@mui/icons-material';

const LeaveBalance = ({ employee, leaveData, snackbar, setSnackbar }) => {
  const currentYear = new Date().getFullYear();
  const previousYear = currentYear - 1;
  console.log('Leave Data:', leaveData);
  console.log('Employee Data:', employee);

  // Enrich with previous/current year data
  const enhancedLeaveData = leaveData.map(leave => {
    const total = leave.total || 0; // current year's annual vacation

    // Assume previous year had same total allowance
    const previousTotal = total;
    const previousBalance = leave.previousBalance || 0;
    const previousConsumed = Math.max(0, previousTotal - previousBalance);
    const previousPercentage = previousTotal > 0 
      ? Math.min(100, Math.max(0, (previousBalance / previousTotal) * 100)) 
      : 0;

    const currentBalance = leave.remaining || 0;
    const currentConsumed = Math.max(0, total - currentBalance);
    const currentPercentage = total > 0 
      ? Math.min(100, Math.max(0, (currentBalance / total) * 100)) 
      : 0;

    return {
      ...leave,
      previous: {
        remaining: previousBalance,
        total: previousTotal,
        consumed: previousConsumed,
        percentage: previousPercentage
      },
      current: {
        remaining: currentBalance,
        total: total,
        consumed: currentConsumed,
        percentage: currentPercentage
      }
    };
  });

  return (
    <Box sx={{ width: '100%', p: 0 }}>
      {/* Employee Info Header */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Typography variant="h4" gutterBottom>
          My Leave Balance
        </Typography>
        <Typography variant="h6">
          {employee ? `${employee.firstName} ${employee.lastName}` : 'Employee'}
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          {employee?.department?.name} • {employee?.position}
        </Typography>
      </Paper>

      {/* Leave Balance Cards — using original decoration */}
      <Grid sx={{ width: '100%' }} container spacing={3}>
        {enhancedLeaveData.map((leave) => {
          const IconComponent = leave.icon;

          // Business rule: use previous year first
          const effectiveRemaining = leave.previous.remaining + leave.current.remaining;

          const effectiveTotal = leave.previous.total + leave.previous.remaining;

          const effectiveConsumed = effectiveTotal - effectiveRemaining;
          const effectivePercentage = effectiveTotal > 0 ? (effectiveRemaining / effectiveTotal) * 100 : 0;

          return (
            <Grid sx={{ width: '31.5%' }} item xs={12} md={4} key={leave.type}>
              <Card 
                sx={{ 
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: '50%',
                        backgroundColor: `${leave.color}20`,
                        color: leave.color,
                        mr: 2
                      }}
                    >
                      <IconComponent />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {leave.label}
                    </Typography>
                  </Box>

                  {/* Unified progress bar using effective balance */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        Remaining
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {effectiveRemaining} / {effectiveTotal} days
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={effectivePercentage}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: '#f0f0f0',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: leave.color,
                          borderRadius: 4
                        }
                      }}
                    />
                  </Box>

                  {/* Days Left / Used — based on effective balance */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h3" sx={{ color: leave.color, fontWeight: 700 }}>
                        {effectiveRemaining}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Days Left
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h3" sx={{ color: '#f44336', fontWeight: 700 }}>
                        {effectiveConsumed}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Days Used
                      </Typography>
                    </Box>
                  </Box>

                  {/* Warnings based on effective remaining */}
                  {effectiveRemaining <= 2 && effectiveRemaining > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      Low balance! Only {effectiveRemaining} days left.
                    </Alert>
                  )}
                  {effectiveRemaining === 0 && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      No {leave.label.toLowerCase()} days remaining.
                    </Alert>
                  )}

                  {/* Optional: Transparent footnote showing both years */}
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed #eee', fontSize: '0.8rem', color: '#666' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>{previousYear}: {leave.previous.remaining} left</span>
                      <span>{currentYear}: {leave.current.remaining} left</span>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Summary Card — only current year totals (as in original logic) */}
      <Card sx={{ mt: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <TrendingDown sx={{ mr: 1, color: '#666' }} />
            Leave Summary
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#e8f5e8', borderRadius: 2 }}>
                <Typography variant="h3" sx={{ color: '#4CAF50', fontWeight: 700 }}>
                  {enhancedLeaveData.reduce((sum, leave) => sum + leave.current.remaining, 0)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Days Remaining ({currentYear})
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'var(--warning-light, #fff3e0)', borderRadius: 2 }}>
                <Typography variant="h3" sx={{ color: '#FF9800', fontWeight: 700 }}>
                  {enhancedLeaveData.reduce((sum, leave) => sum + leave.current.consumed, 0)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Days Used ({currentYear})
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f3e5f5', borderRadius: 2 }}>
                <Typography variant="h3" sx={{ color: '#9C27B0', fontWeight: 700 }}>
                  {enhancedLeaveData.reduce((sum, leave) => sum + leave.current.total, 0)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Annual Allowance ({currentYear})
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LeaveBalance;

// import React from 'react';
// import {
//   Box,
//   Paper,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   LinearProgress,
//   Alert,
//   Snackbar
// } from '@mui/material';
// import { TrendingDown } from '@mui/icons-material';

// const LeaveBalance = ({
//   employee,
//   leaveData,
//   snackbar,
//   setSnackbar
// }) => {
//   return (
//     <Box sx={{ width: '100%',p: 0}}>
//       {/* Employee Info Header */}
//       <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
//         <Typography variant="h4" gutterBottom>
//           My Leave Balance
//         </Typography>
//         <Typography variant="h6">
//           {employee ? `${employee.firstName} ${employee.lastName}` : 'Employee'}
//         </Typography>
//         <Typography variant="body1" sx={{ opacity: 0.9 }}>
//           {employee?.department?.name} • {employee?.position}
//         </Typography>
//       </Paper>

//       {/* Leave Balance Cards */}
//       <Grid sx={{ width: '100%'}} container spacing={3}>
//         {leaveData.map((leave) => {
//           const IconComponent = leave.icon;
//           return (
//             <Grid sx={{ width: '31.5%' }} item xs={12} md={4} key={leave.type}>
//               <Card 
//                 sx={{ 
//                   height: '100%',
//                   transition: 'transform 0.2s, box-shadow 0.2s',
//                   '&:hover': {
//                     transform: 'translateY(-4px)',
//                     boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
//                   }
//                 }}
//               >
//                 <CardContent sx={{ p: 3}}>
//                   <Box sx={{ display: 'flex', alignItems: 'center', mb: 2}}>
//                     <Box
//                       sx={{
//                         p: 1.5,
//                         borderRadius: '50%',
//                         backgroundColor: `${leave.color}20`,
//                         color: leave.color,
//                         mr: 2
//                       }}
//                     >
//                       <IconComponent />
//                     </Box>
//                     <Typography variant="h6" sx={{ fontWeight: 600 }}>
//                       {leave.label}
//                     </Typography>
//                   </Box>

//                   <Box sx={{ mb: 3 }}>
//                     <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
//                       <Typography variant="body2" color="textSecondary">
//                         Remaining
//                       </Typography>
//                       <Typography variant="body2" color="textSecondary">
//                         {leave.remaining} / {leave.total} days
//                       </Typography>
//                     </Box>
//                     <LinearProgress
//                       variant="determinate"
//                       value={leave.percentage}
//                       sx={{
//                         height: 8,
//                         borderRadius: 4,
//                         backgroundColor: '#f0f0f0',
//                         '& .MuiLinearProgress-bar': {
//                           backgroundColor: leave.color,
//                           borderRadius: 4
//                         }
//                       }}
//                     />
//                   </Box>

//                   <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
//                     <Box sx={{ textAlign: 'center' }}>
//                       <Typography variant="h4" sx={{ color: leave.color, fontWeight: 700 }}>
//                         {leave.remaining}
//                       </Typography>
//                       <Typography variant="caption" color="textSecondary">
//                         Days Left
//                       </Typography>
//                     </Box>
//                     <Box sx={{ textAlign: 'center' }}>
//                       <Typography variant="h4" sx={{ color: '#f44336', fontWeight: 700 }}>
//                         {leave.consumed}
//                       </Typography>
//                       <Typography variant="caption" color="textSecondary">
//                         Days Used
//                       </Typography>
//                     </Box>
//                   </Box>

//                   {leave.remaining <= 2 && leave.remaining > 0 && (
//                     <Alert severity="warning" sx={{ mt: 2 }}>
//                       Low balance! Only {leave.remaining} days left.
//                     </Alert>
//                   )}
                  
//                   {leave.remaining === 0 && (
//                     <Alert severity="error" sx={{ mt: 2 }}>
//                       No {leave.label.toLowerCase()} days remaining.
//                     </Alert>
//                   )}
//                 </CardContent>
//               </Card>
//             </Grid>
//           );
//         })}
//       </Grid>

//       {/* Summary Card */}
//       <Card sx={{ mt: 3 }}>
//         <CardContent sx={{ p: 3 }}>
//           <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
//             <TrendingDown sx={{ mr: 1, color: '#666' }} />
//             Leave Summary
//           </Typography>
//           <Grid container spacing={3}>
//             <Grid item xs={12} sm={4}>
//               <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#e8f5e8', borderRadius: 2 }}>
//                 <Typography variant="h3" sx={{ color: '#4CAF50', fontWeight: 700 }}>
//                   {leaveData.reduce((sum, leave) => sum + leave.remaining, 0)}
//                 </Typography>
//                 <Typography variant="body2" color="textSecondary">
//                   Total Days Remaining
//                 </Typography>
//               </Box>
//             </Grid>
//             <Grid item xs={12} sm={4}>
//               <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'var(--warning-light, #fff3e0)', borderRadius: 2 }}>
//                 <Typography variant="h3" sx={{ color: '#FF9800', fontWeight: 700 }}>
//                   {leaveData.reduce((sum, leave) => sum + leave.consumed, 0)}
//                 </Typography>
//                 <Typography variant="body2" color="textSecondary">
//                   Total Days Used
//                 </Typography>
//               </Box>
//             </Grid>
//             <Grid item xs={12} sm={4}>
//               <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f3e5f5', borderRadius: 2 }}>
//                 <Typography variant="h3" sx={{ color: '#9C27B0', fontWeight: 700 }}>
//                   {leaveData.reduce((sum, leave) => sum + leave.total, 0)}
//                 </Typography>
//                 <Typography variant="body2" color="textSecondary">
//                   Total Annual Allowance
//                 </Typography>
//               </Box>
//             </Grid>
//           </Grid>
//         </CardContent>
//       </Card>

//       {/* Snackbar */}
//       <Snackbar
//         open={snackbar.open}
//         autoHideDuration={4000}
//         onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
//       >
//         <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
//           {snackbar.message}
//         </Alert>
//       </Snackbar>
//     </Box>
//   );
// };

// export default LeaveBalance;
